/**
 * Service-worker registration (W45-F1).
 *
 * Registers `/service-worker.js` on production builds only. Dev (Vite
 * HMR) skips registration entirely so a stale SW can't intercept HMR
 * websockets.
 *
 * Call once from `main.tsx`. Safe to call before React mounts.
 */

export interface RegistrationResult {
  status: 'registered' | 'unsupported' | 'skipped-dev' | 'failed';
  registration?: ServiceWorkerRegistration;
  error?: unknown;
}

export async function registerServiceWorker(opts?: {
  enabled?: boolean;
}): Promise<RegistrationResult> {
  const enabled = opts?.enabled ?? import.meta.env.PROD;
  if (!enabled) return { status: 'skipped-dev' };
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return { status: 'unsupported' };
  }
  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
    });
    return { status: 'registered', registration };
  } catch (error) {
    return { status: 'failed', error };
  }
}

/**
 * Manually trigger an SW update + reload. Used by an upgrade-available
 * UI prompt downstream; not wired into the initial PR.
 */
export async function applyServiceWorkerUpdate(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration?.waiting) return;
  registration.waiting.postMessage('SKIP_WAITING');
  await new Promise<void>((resolve) => {
    navigator.serviceWorker.addEventListener('controllerchange', () => resolve(), { once: true });
  });
  window.location.reload();
}
